import { T } from '@/i18n/LocaleProvider';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Button } from '@/components';
import { MONO_FONT } from '@/theme';

function IVATag() {
  return (
    <Box
      component="span"
      sx={{
        fontFamily: MONO_FONT,
        fontSize: '10px',
        letterSpacing: '0.04em',
        ml: 1,
        color: 'text.muted',
      }}
    >
      <T>+ IVA</T>
    </Box>
  );
}

export default function PlanCard({
  eyebrow,
  name,
  description,
  price,
  priceUSD,
  period,
  features = [],
  cta = 'Comenzar',
  href = '/contacto',
  featured = false,
  currency = 'CLP',
  id,
  layout = 'vertical',
}) {
  const displayPrice = currency === 'USD' && priceUSD ? priceUSD : price;
  const wide = layout === 'wide';
  const showIVA = displayPrice && displayPrice !== 'A medida';

  const eyebrowEl = eyebrow && (
    <Typography
      sx={{
        fontFamily: MONO_FONT,
        fontSize: '10.5px',
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: featured ? 'primary.darker' : 'text.accent',
        fontWeight: 600,
        mb: 1.5,
      }}
    >
      <T>{eyebrow}</T>
    </Typography>
  );

  const nameEl = (
    <Typography
      variant="h3"
      component="h3"
      sx={{
        color: featured ? 'primary.darkest' : 'text.primary',
        mb: 1,
      }}
    >
      <T>{name}</T>
    </Typography>
  );

  const descriptionEl = description && (
    <Typography
      sx={{
        fontSize: '14px',
        lineHeight: 1.55,
        color: featured ? 'primary.darker' : 'grey.600',
        mb: wide ? 0 : 2,
      }}
    >
      <T>{description}</T>
    </Typography>
  );

  const ctaEl = (
    <Button
      href={href}
      variant={featured ? 'contained' : 'ghost'}
      fullWidth={!wide}
      sx={
        wide
          ? { flexShrink: 0, width: { xs: '100%', md: 'auto' } }
          : { mt: 'auto' }
      }
      data-gtm-event="demo_request"
      data-gtm-source={`planes_card_${id ?? 'unknown'}`}
    >
      <T>{cta}</T>
    </Button>
  );

  if (wide) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 3, md: 4 },
          p: { xs: '28px', md: '32px 28px' },
          border: '1px solid',
          borderColor: featured ? 'primary.main' : '#e2e5e3',
          borderRadius: 2,
          bgcolor: featured ? 'primary.lightest' : 'transparent',
        }}
      >
        <Box sx={{ flexShrink: 0, width: { xs: '100%', md: 280 } }}>
          <T>{eyebrowEl}</T>
          <T>{nameEl}</T>
          <T>{descriptionEl}</T>
          <Typography
            sx={{
              fontSize: '32px',
              fontWeight: 400,
              letterSpacing: '-0.03em',
              lineHeight: 1,
              color: featured ? 'primary.darkest' : 'text.primary',
              mt: 1.5,
            }}
          >
            <T>{displayPrice}</T>
            {showIVA && <IVATag />}
          </Typography>
          {period && (
            <Typography
              sx={{
                fontFamily: MONO_FONT,
                fontSize: '11px',
                color: featured ? 'primary.darker' : 'text.muted',
                letterSpacing: '0.04em',
                mt: 0.5,
              }}
            >
              <T>{period}</T>
            </Typography>
          )}
        </Box>

        <Box
          component="ul"
          sx={{
            listStyle: 'none',
            p: 0,
            m: 0,
            flex: 1,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
            gap: '10px 24px',
          }}
        >
          {features.map((f) => (
            <Box
              component="li"
              key={f}
              sx={{
                display: 'flex',
                gap: '10px',
                alignItems: 'flex-start',
                fontSize: '14px',
                lineHeight: 1.4,
                color: featured ? 'primary.darker' : 'grey.600',
                pl: 2,
                position: 'relative',
                '&::before': {
                  content: '"→"',
                  position: 'absolute',
                  left: 0,
                  color: featured ? 'primary.darkest' : 'text.accent',
                  fontFamily: MONO_FONT,
                  fontSize: '12px',
                },
              }}
            >
              <T>{f}</T>
            </Box>
          ))}
        </Box>

        <T>{ctaEl}</T>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        p: { xs: '28px', md: '32px 28px' },
        border: '1px solid',
        borderColor: featured ? 'primary.main' : '#e2e5e3',
        borderRadius: 2,
        bgcolor: featured ? 'primary.lightest' : 'transparent',
        position: 'relative',
      }}
    >
      <T>{eyebrowEl}</T>
      <T>{nameEl}</T>
      <T>{descriptionEl}</T>

      <Box
        sx={{
          py: 1.5,
          borderTop: '1px solid',
          borderBottom: '1px solid',
          borderColor: featured ? 'rgba(0,159,139,0.2)' : 'grey.100',
          mb: 2,
        }}
      >
        {displayPrice && (
          <Typography
            sx={{
              fontSize: '32px',
              fontWeight: 400,
              letterSpacing: '-0.03em',
              lineHeight: 1,
              color: featured ? 'primary.darkest' : 'text.primary',
            }}
          >
            <T>{displayPrice}</T>
            {showIVA && <IVATag />}
          </Typography>
        )}
        {period && (
          // Sin precio (programa de agencias) el período pasa a ser la línea principal del bloque.
          <Typography
            sx={
              displayPrice
                ? {
                    fontFamily: MONO_FONT,
                    fontSize: '11px',
                    color: featured ? 'primary.darker' : 'text.muted',
                    letterSpacing: '0.04em',
                    mt: 0.5,
                  }
                : {
                    fontSize: '16px',
                    fontWeight: 500,
                    color: featured ? 'primary.darkest' : 'text.primary',
                  }
            }
          >
            <T>{period}</T>
          </Typography>
        )}
      </Box>

      <Box
        component="ul"
        sx={{
          listStyle: 'none',
          p: 0,
          m: 0,
          mb: 3,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
        }}
      >
        {features.map((f) => (
          <Box
            component="li"
            key={f}
            sx={{
              display: 'flex',
              gap: '10px',
              alignItems: 'flex-start',
              py: '8px',
              borderBottom: '1px solid',
              borderColor: featured ? 'rgba(0,159,139,0.15)' : 'grey.100',
              fontSize: '14px',
              lineHeight: 1.4,
              color: featured ? 'primary.darker' : 'grey.600',
              '&:last-child': { borderBottom: 'none' },
              pl: 2,
              position: 'relative',
              '&::before': {
                content: '"→"',
                position: 'absolute',
                left: 0,
                color: featured ? 'primary.darkest' : 'text.accent',
                fontFamily: MONO_FONT,
                fontSize: '12px',
              },
            }}
          >
            <T>{f}</T>
          </Box>
        ))}
      </Box>

      <T>{ctaEl}</T>
    </Box>
  );
}
